/**
 * /join?ref=TOKEN — client invite entry point.
 *
 * An agent shares this URL with a prospective client. Resolves the invite
 * token, then redirects into /b/:slug/a/:agentId — the SAME rich
 * agent-landing page (photo, dual-license broker cards, compliance footer)
 * the agent's own share link uses, rather than a separate, plainer "You've
 * been invited" card (Checkpoint #16 — kept the two pages out of sync until
 * now). Falls back to the plain brokerage page (/b/:slug) if the inviting
 * user has no agent profile in this tenant, and shows an inline error for an
 * invalid/expired/used token — those states have no page to redirect into.
 */

import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { saveOnboardingAgent } from "../home/onboardingAgent";
import { identityApi } from "./passkeyApi";
import "./SignIn.css";
import { PublicFooter } from "../../shell/PublicFooter";

type State = { kind: "loading" } | { kind: "error"; message: string };

export function JoinPage() {
  const [params] = useSearchParams();
  const token = params.get("ref") ?? "";
  const [state, setState] = useState<State>({ kind: "loading" });
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", message: "No invite token found in the URL." });
      return;
    }
    identityApi
      .getInvite(token)
      .then((invite) => {
        // Spec 023 — the invite's agent, brokerage and token, before the redirect. The
        // landing page re-saves its own agent and keeps the token while the visitor
        // stays in this brokerage; sign-up sends it so the server records the invite.
        saveOnboardingAgent({
          agentId: invite.agent_id,
          agentTenantId: invite.tenant_id,
          agentName: invite.agent_name,
          agentPhone: invite.agent_phone,
          inviteToken: invite.token,
        });
        const path = invite.agent_profile_id
          ? `/b/${invite.slug}/a/${invite.agent_profile_id}`
          : `/b/${invite.slug}`;
        void navigate(path, {
          replace: true,
          state: { inviteToken: invite.token },
        });
      })
      .catch((err: unknown) =>
        setState({
          kind: "error",
          message:
            err instanceof Error ? err.message : "Invite link is invalid.",
        }),
      );
  }, [token, navigate]);

  if (state.kind === "error") {
    return (
      <main className="signin">
        <section className="signin__card">
          <h1>Invite not found</h1>
          <div className="signin__error" role="alert">
            <p>{state.message}</p>
          </div>
          <p className="signin__footnote">
            Ask your agent to send you a fresh invite link.
          </p>
        </section>
        <PublicFooter />
      </main>
    );
  }

  return (
    <main className="signin">
      <section className="signin__card">
        <div className="signin__brand">
          <Loader2 className="signin__spin" size={28} aria-hidden />
          <span>Loading invite…</span>
        </div>
      </section>
      <PublicFooter />
    </main>
  );
}
